import type { GithubSettings, GithubMainBranchTestMode, GithubPrTestMode } from '../data/projectTypes'
import { MAIN_BRANCH_TEST_OPTIONS, PR_TEST_OPTIONS } from './githubTestingOptions'

// Shared logic for the two GitHub testing questions (main-branch deployments + pull requests) asked
// both by the chat onboarding walkthrough (OnboardingChat) and the running-project connect card
// (ProjectGithubChatCard). Keeping the answer shape, defaults, and the GithubSettings blob here means
// both surfaces ask the same thing and persist identical settings — see GithubTestingQuestions for the
// shared UI that collects these answers.

// The user's answers to the two testing questions. `mainBranchDeploymentUrl` is only meaningful (and
// only non-empty) when `mainBranchTesting === 'detect'`.
export type GithubTestingAnswers = {
  mainBranchTesting: GithubMainBranchTestMode
  mainBranchDeploymentUrl: string
  prTesting: GithubPrTestMode
}

export const DEFAULT_GITHUB_TESTING_ANSWERS: GithubTestingAnswers = {
  mainBranchTesting: 'none',
  mainBranchDeploymentUrl: '',
  prTesting: 'none',
}

// Turn the repository forms people naturally type in chat into the canonical URL stored by the
// project. Keep this deliberately strict: the value is used to query GitHub App installation status,
// so a lookalike host or a path that is not exactly owner/repo must not reach that API.
export function normalizeGithubRepositoryUrl(raw: string): string | null {
  const trimmed = raw.trim()
  if (!trimmed) return null

  const shorthand = trimmed.match(/^([A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?)\/([A-Za-z0-9._-]+?)(?:\.git)?\/?$/)
  if (shorthand) return `https://github.com/${shorthand[1]}/${shorthand[2]}`

  const withScheme = /^[a-z][a-z0-9+.-]*:\/\//i.test(trimmed) ? trimmed : `https://${trimmed}`
  try {
    const url = new URL(withScheme)
    if (url.protocol !== 'https:' || (url.hostname !== 'github.com' && url.hostname !== 'www.github.com')) return null
    if (url.username || url.password || url.port || url.search || url.hash) return null
    const parts = url.pathname.split('/').filter(Boolean)
    if (parts.length !== 2) return null
    const [owner, repoWithSuffix] = parts
    const repo = repoWithSuffix?.replace(/\.git$/, '')
    if (!owner || !repo || !/^[A-Za-z0-9](?:[A-Za-z0-9-]*[A-Za-z0-9])?$/.test(owner) || !/^[A-Za-z0-9._-]+$/.test(repo)) return null
    return `https://github.com/${owner}/${repo}`
  } catch {
    return null
  }
}

// Build the GithubSettings blob persisted on create/connect from the repo + the two testing answers.
// file-issues defaults off (it's configured later on the settings page), matching
// both the onboarding wizard and the project-chat connect card.
export function buildGithubSettings(
  repoUrl: string,
  mainBranch: string,
  answers: GithubTestingAnswers,
): GithubSettings {
  return {
    repoUrl,
    mainBranch: mainBranch || 'main',
    mainBranchTesting: answers.mainBranchTesting,
    mainBranchDeploymentUrl:
      answers.mainBranchTesting === 'detect' ? (answers.mainBranchDeploymentUrl.trim() || null) : null,
    prTesting: answers.prTesting,
    fileIssues: false,
    accessTokenSet: false,
  }
}

// Apply new testing answers onto an already-connected project's settings, preserving everything else
// (repo, branch, file-issues, access token). Used when the user changes main-branch
// or PR testing from the chat without re-connecting.
export function applyGithubTestingAnswers(existing: GithubSettings, answers: GithubTestingAnswers): GithubSettings {
  return {
    ...existing,
    mainBranchTesting: answers.mainBranchTesting,
    mainBranchDeploymentUrl:
      answers.mainBranchTesting === 'detect' ? (answers.mainBranchDeploymentUrl.trim() || null) : null,
    prTesting: answers.prTesting,
  }
}

// One-line, human-readable summary of the chosen testing options (for chat confirmations and context).
export function summarizeGithubTesting(answers: GithubTestingAnswers): string {
  const mainLabel = MAIN_BRANCH_TEST_OPTIONS.find((o) => o.value === answers.mainBranchTesting)?.label ?? answers.mainBranchTesting
  const prLabel = PR_TEST_OPTIONS.find((o) => o.value === answers.prTesting)?.label ?? answers.prTesting
  return `Main branch: ${mainLabel} · Pull requests: ${prLabel}`
}

// Human-readable lines describing a project's full GitHub settings, for the chat's project context so
// the agent can inspect and report main-branch / PR testing (not just the repo URL).
export function describeGithubSettings(github: GithubSettings): string[] {
  const mainLabel = MAIN_BRANCH_TEST_OPTIONS.find((o) => o.value === github.mainBranchTesting)?.label ?? github.mainBranchTesting
  const prLabel = PR_TEST_OPTIONS.find((o) => o.value === github.prTesting)?.label ?? github.prTesting
  const lines = [
    `GitHub repo: ${github.repoUrl} (default branch: ${github.mainBranch})`,
    `  • Main-branch testing: ${mainLabel}${github.mainBranchTesting === 'detect' && github.mainBranchDeploymentUrl ? ` (deployment URL: ${github.mainBranchDeploymentUrl})` : ''}`,
    `  • Pull-request testing: ${prLabel}`,
    `  • File GitHub issues for bugs: ${github.fileIssues ? 'yes' : 'no'}`,
  ]
  if (github.authorizationRequired) lines.push('  • Repository authority: reconnect required; GitHub events, checks and credentials are disabled until the repository is reconnected')
  return lines
}

// The binding the server acts on: a repository whose authority the server reports as verified. A binding
// that still requires authorization, or a projection without that field, does not count as connected.
export function isGithubConnected(github: Pick<GithubSettings, 'repoUrl' | 'authorizationRequired'> | null | undefined): boolean {
  return !!github?.repoUrl && github.authorizationRequired === false
}

export function githubAuthorizationUrl(repo: string): string {
  const params = new URLSearchParams({ repo, returnTo: window.location.pathname + window.location.search })
  return `/api/github-authorize?${params}`
}
